import {Injectable, signal, WritableSignal} from '@angular/core';
import {CommunicationService} from '../../http/communication.service';
import {environment} from '../../../environments/environment';
import {
  FreemiumFields,
  HubspotFieldPayload,
  HubspotFields,
  HubspotFieldKey,
  HubspotFormPhase,
  HubspotUtm,
  ObjectTypeId
} from './hubspot.types';

@Injectable({
  providedIn: 'root'
})
export class HubspotService {

  private readonly basePath = environment.hubspotBaseUrl;
  private readonly portalId = environment.hubspotPortalId;
  private fields$: WritableSignal<FreemiumFields> = signal({
    firstname: '', lastname: '', email: '', userId: '', signupToken: ''
  })

  constructor(private readonly communicationService: CommunicationService) {
  }

  public updateFields(fields: Partial<FreemiumFields>): void {
    this.fields$.update((curr) => ({...curr, ...fields}));
  }

  public getFields(): FreemiumFields {
    return this.fields$();
  }

  public async submitToHubspot(phase: HubspotFormPhase, form: HubspotFields, url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const utmData = this.getUtmFieldsFromUrl(url);
      const fields = this.formDataToFieldPayload({...form, ...utmData});
      const hubspotUrl = this.buildSubmitFormUrl(phase);
      this.communicationService.postRequest(hubspotUrl, {fields: fields}, {skipAuth: true}).subscribe({
        next: () => resolve(),
        error: (error) => reject(new Error(`Error submitting to Hubspot: ${error}`))
      });
    })
  }

  private formDataToFieldPayload(form: HubspotFields): HubspotFieldPayload[] {
    const mapName = (name: string): HubspotFieldKey => {
      switch (name) {
        case 'userId':
          return 'hub_auth0_id';
        case 'signupToken':
          return 'hub_signup_token';
        default:
          return name as HubspotFieldKey;
      }
    }
    return Object.entries(form)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => ({
        objectTypeId: ObjectTypeId.CONTACTS,
        name: mapName(key),
        value: String(value),
      }));
  }

  private buildSubmitFormUrl(phase: HubspotFormPhase): string {
    const formId = this.getFormId(phase);
    return `${this.basePath}/submissions/v3/integration/submit/${this.portalId}/${formId}`;
  }

  private getUtmFieldsFromUrl(url?: string): Partial<HubspotUtm> {
    if (!url) return {};
    try {
      const {searchParams} = new URL(url, window.location.origin);
      const utmFields: Partial<HubspotUtm> = {};

      const keys: (keyof HubspotUtm)[] = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content'
      ];

      keys.forEach((key) => {
        const value = searchParams.get(key);
        if (value) {
          utmFields[key] = value;
        }
      });

      return utmFields;
    } catch (error) {
      console.error("Invalid URL provided to getUtmFieldsFromUrl", error);
      return {};
    }
  }

  private getFormId(phase: HubspotFormPhase): string {
    switch (phase) {
      case HubspotFormPhase.STEP_ONE:
        return environment.hubspotStepOneFormId;
      case HubspotFormPhase.STEP_THREE:
        return environment.hubspotStepThreeFormId;
    }
  }
}
